import { useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Breadcrumb, Button, Steps, Tag } from 'antd';
import {
    ArrowLeftOutlined,
    FormOutlined,
    GiftOutlined,
    TrophyOutlined,
    WarningOutlined,
} from '@ant-design/icons';

import { IGiveawayAuto } from '../../../../interfaces';
import { ThemeContext } from '../../../../contexts/ThemeContext';
import PageHeader from '../../../../components/PageHeader';

import useGiveawayForm from './useGiveawayForm';
import GiveawayDataStep from './Steps/GiveawayDataStep';
import AwardsStep from './Steps/AwardsStep';

interface IProps {
    action: 'Adicionar' | 'Editar';
    giveawayAuto?: IGiveawayAuto;
    createdGiveaway?: IGiveawayAuto;
    onSubmit: (user: IGiveawayAuto) => Promise<void>;
}

export default function GiveawayForm({ action, giveawayAuto, onSubmit, createdGiveaway }: IProps) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';
    const navigate = useNavigate();

    const {
        currentStep,
        handleClickGiveawayStep,
        handleClickGiveawayStepWhenCreating,
        handleClickAwardsStep,
        handleSubmitGiveawayDataStepFinished,
        isInvalid,
        handleAwardsListLengthChange,
        refIsEditing,
        canEdit,
    } = useGiveawayForm({ giveawayAuto, createdGiveaway });

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* External Breadcrumb Card */}
            <div
                style={{
                    background: isDark ? '#141414' : '#ffffff',
                    borderRadius: 10,
                    border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                    boxShadow: isDark
                        ? '0 4px 20px rgba(0, 0, 0, 0.35)'
                        : '0 2px 10px rgba(0, 0, 0, 0.03)',
                    padding: '10px 16px',
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                <Breadcrumb
                    items={[
                        {
                            title: <Link to="/sorteios/auto">Sorteios</Link>,
                        },
                        {
                            title: `${action} Sorteio`,
                        },
                    ]}
                />
            </div>

            <PageHeader
                icon={<TrophyOutlined />}
                iconBg="linear-gradient(135deg, #1677ff 0%, #722ed1 100%)"
                title={`${action} Sorteio (Importação Automática)`}
                tag={
                    <Tag
                        color={currentStep === 0 ? 'blue' : 'purple'}
                        style={{ fontWeight: 600, borderRadius: 6, padding: '2px 8px' }}
                    >
                        Etapa {currentStep + 1} de 2: {currentStep === 0 ? 'Dados do Sorteio' : 'Premiação'}
                    </Tag>
                }
                subtitle="Configure os dados gerais, prazos e os prêmios a serem distribuídos no sorteio com importação automática."
                actions={
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate('/sorteios/auto')}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            borderRadius: 8,
                            fontWeight: 500,
                        }}
                    >
                        Voltar para Sorteios
                    </Button>
                }
            />

            {/* Stepper Wizard Card */}
            <div
                style={{
                    background: isDark ? '#141414' : '#ffffff',
                    borderRadius: 10,
                    border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                    boxShadow: isDark
                        ? '0 4px 20px rgba(0, 0, 0, 0.35)'
                        : '0 2px 10px rgba(0, 0, 0, 0.03)',
                    padding: '14px 24px',
                    display: 'flex',
                    justifyContent: 'center',
                }}
            >
                <div style={{ width: '100%', maxWidth: 520 }}>
                    <Steps
                        current={currentStep}
                        size="small"
                        items={[
                            {
                                title: <span style={{ fontWeight: 600 }}>1. Dados do Sorteio</span>,
                                description: (
                                    <span style={{ fontSize: 12, color: isDark ? '#8c8c8c' : '#64748b' }}>
                                        Período, datas e regras
                                    </span>
                                ),
                                icon: <FormOutlined />,
                                onClick: createdGiveaway
                                    ? () => handleClickGiveawayStepWhenCreating(createdGiveaway.id)
                                    : giveawayAuto
                                    ? handleClickGiveawayStep
                                    : undefined,
                                style: {
                                    cursor: giveawayAuto || createdGiveaway ? 'pointer' : 'default',
                                },
                            },
                            {
                                title: <span style={{ fontWeight: 600 }}>2. Prêmios</span>,
                                description: (
                                    <span style={{ fontSize: 12, color: isDark ? '#8c8c8c' : '#64748b' }}>
                                        Premiação e ordenação
                                    </span>
                                ),
                                icon: <GiftOutlined />,
                                onClick: giveawayAuto ? handleClickAwardsStep : undefined,
                                disabled: !giveawayAuto && !createdGiveaway,
                                style: {
                                    cursor: giveawayAuto || createdGiveaway ? 'pointer' : 'default',
                                },
                            },
                        ]}
                    />
                </div>
            </div>

            {currentStep === 0 && isInvalid && (
                <Alert
                    type="warning"
                    showIcon
                    icon={<WarningOutlined />}
                    message="Sorteio pendente de validação"
                    description="Cadastre pelo menos um prêmio na etapa seguinte para validar e ativar este sorteio no sistema."
                    style={{
                        borderRadius: 10,
                        border: '1px solid #ffe58f',
                    }}
                />
            )}

            {currentStep === 0 && (
                <GiveawayDataStep
                    onSubmit={onSubmit}
                    giveawayAuto={giveawayAuto}
                    onSubmitGiveawayDataStepFinished={handleSubmitGiveawayDataStepFinished}
                    refIsEditing={refIsEditing}
                    canEdit={canEdit}
                />
            )}

            {currentStep === 1 && (
                <AwardsStep
                    giveawayAuto={giveawayAuto || createdGiveaway}
                    onAwardsListLengthChange={handleAwardsListLengthChange}
                    refIsEditing={refIsEditing}
                    canEdit={canEdit}
                />
            )}
        </div>
    );
}

